/**
 * CookieConsentBanner - באנר הסכמת עוגיות
 * Shows on first visit until the user makes a choice; records it in localStorage
 * with the policy version so we can re-prompt when the policy changes.
 * Accessible: labelled region, real buttons, keyboard-operable, links to /cookies.
 *
 * Currently the app uses only essential + functional storage (no analytics/marketing),
 * so "reject non-essential" records the preference and is the hook to gate any future
 * analytics behind. Mount once at the app root.
 */

import { useEffect, useState } from 'react'
import { Cookie } from 'lucide-react'
import { Link } from 'react-router-dom'

const STORAGE_KEY = 'binat-cookie-consent'
// Bump when the cookie policy changes substantively → users are re-prompted.
const CONSENT_VERSION = '1.0'

type Choice = 'all' | 'essential'

export function getCookieConsent(): { version: string; choice: Choice } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const saved = getCookieConsent()
    if (!saved || saved.version !== CONSENT_VERSION) setVisible(true)
  }, [])

  const decide = (choice: Choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: CONSENT_VERSION, choice, date: new Date().toISOString() })
      )
    } catch {
      /* storage unavailable — banner simply won't persist */
    }
    setVisible(false)
  }

  if (!visible) return null

  return (
    <section
      aria-label="הודעת עוגיות"
      className="fixed bottom-0 inset-x-0 z-[65] bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
    >
      <div className="max-w-5xl mx-auto p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700 flex items-start gap-2">
          <Cookie size={20} className="text-primary-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            אנו משתמשים בעוגיות וברכיבי אחסון הכרחיים לתפעול השירות. למידע נוסף ראה את{' '}
            <Link to="/cookies" className="text-primary-700 underline hover:text-primary-800">מדיניות העוגיות</Link>.
          </span>
        </p>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => decide('essential')}
            className="btn btn-outline whitespace-nowrap"
          >
            רק הכרחיים
          </button>
          <button
            type="button"
            onClick={() => decide('all')}
            className="btn btn-primary whitespace-nowrap"
          >
            אישור
          </button>
        </div>
      </div>
    </section>
  )
}
